import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { usePolicyAutomation } from '../../providers/GroupPolicyAutomationProvider';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api, { noBrowserCache } from '../../components/services/apiService';
import { usePermissions } from '../../hooks/usePermissions';

interface TenantGroup {
  tenantGroupName: string;
  tenantGroupID: string;
}

interface PolicyDetailFormProps {
  policyId?: string;
  isEditMode?: boolean;
}

const PolicyDetailForm: React.FC<PolicyDetailFormProps> = ({ policyId, isEditMode = false }) => {
  const [groupsApi, setGroupsApi] = useState<TenantGroup[]>([]);

  const { PolicyAutomationData, setPolicyAutomation, resetPolicyAutomationData } = usePolicyAutomation();

  const [initialValues, setInitialValues] = useState({ policyName: PolicyAutomationData?.policyName || '' });
  const [selectedGroups, setSelectedGroups] = useState<TenantGroup[]>(
    (PolicyAutomationData.tenantGroups || []).filter((g) => g && g.tenantGroupID && g.tenantGroupName),
  );
  const { selectedTenantId } = useTenantSelection();
  const navigate = useNavigate();
  const auth = useAuth();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');

  let tenantId = selectedTenantId;

  // Fetch group options
  const getTenantGroups = async () => {
    try {
      const token = auth?.userData?.access_token;
      if (!token) return;

      const { data } = await api.get(`${Project.api}tenantservice/TenantGroup/${tenantId}/groups`, noBrowserCache());

      const apiGroups: TenantGroup[] =
        data?.data?.[0]?.tenantGroupList?.map((g: any) => ({
          tenantGroupName: g.groupName,
          tenantGroupID: g.tenantGroupId,
        })) ?? [];

      setGroupsApi(apiGroups);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  // Fetch policy data if in edit mode
  const getPolicyDetails = async () => {
    try {
      const token = auth?.userData?.access_token;
      if (!token || !policyId) return;

      const response = await api.get(
        `${Project.api}grouppolicyservice/GroupPolicyAutomation/getgrouppolicyautomation/${tenantId}/${policyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const policy = response.data?.data[0];
      const newActivationDate = new Date(new Date(new Date().setDate(new Date().getDate() + 2)).setHours(0, 0, 0, 0)).toISOString();
      setInitialValues({ policyName: policy.policyName });
      setSelectedGroups((policy.tenantGroups || []).filter((g: TenantGroup) => g && g.tenantGroupID && g.tenantGroupName));
      setPolicyAutomation((prev: any) => ({
        ...prev,
        ...policy,
        activationDate: newActivationDate,
      }));
    } catch (error) {
      console.error('Error loading policy:', error);
    }
  };

  useEffect(() => {
    getTenantGroups();
    if (isEditMode && policyId) {
      getPolicyDetails();
      setPolicyAutomation((prev: any) => ({
        ...prev,
        groupPolicyAutomationId: policyId,
      }));
    }
  }, [isEditMode, policyId]);

  useEffect(() => {
    if (PolicyAutomationData.tenantGroups?.length) {
      setSelectedGroups(PolicyAutomationData.tenantGroups.filter((g) => g && g.tenantGroupID && g.tenantGroupName));
    }
  }, []);

  const validationSchema = Yup.object({
    policyName: Yup.string().required('Policy name is required'),
  });

  const addGroup = (groupId: string) => {
    const found = groupsApi.find((g) => g.tenantGroupID === groupId);
    if (found && !selectedGroups.some((g) => g.tenantGroupID === groupId)) {
      setSelectedGroups((prev) => [...prev, found]);
    }
  };

  const removeGroup = (groupId: string) => {
    setSelectedGroups((prev) => prev.filter((g) => g.tenantGroupID !== groupId));
  };

  const handleDiscard = () => {
    resetPolicyAutomationData();
    navigate('/groupPolicy');
  };

  return (
    <div
      style={{
        background: '#1a1a1a',
        padding: '2rem',
        borderRadius: '12px',
        color: '#fff',
        fontFamily: 'Arial',
      }}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={readOnly ? undefined : validationSchema}
        onSubmit={async (values) => {
          if (readOnly) {
            navigate('/groupPolicy/ThirdParty');
            return;
          }

          const token = auth?.userData?.access_token;
          if (!token) return;

          try {
            setPolicyAutomation((prev) => ({
              ...prev,
              policyName: values.policyName,
              tenantGroups: selectedGroups,
            }));
            navigate('/groupPolicy/ThirdParty');
          } catch (err) {
            console.error('Error saving policy:', err);
          }
        }}
      >
        {({ values }) => (
          <Form>
            {/* Policy Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Name</label>
              <Field name="policyName" type="text" placeholder="Enter policy name" style={inputStyle} readOnly={readOnly} />
              <ErrorMessage name="policyName" component="div" style={{ color: '#f33', marginTop: '0.3rem' }} />
            </div>

            {/* Group Dropdown */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Choose group</label>
              <select value="" onChange={(e) => addGroup(e.target.value)} style={inputStyle} disabled={readOnly}>
                <option value="">-- Select a group --</option>
                {groupsApi.map((g) => (
                  <option key={g.tenantGroupID} value={g.tenantGroupID}>
                    {g.tenantGroupName}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Group Tags */}
            <div style={{ marginBottom: '1rem' }}>
              <label>Selected group</label>
              <div
                style={{
                  ...tagContainerStyle,
                  borderBottom: selectedGroups.length ? '.1px solid #2f2' : 'none',
                }}
              >
                {selectedGroups
                  .filter((g) => g && g.tenantGroupID && g.tenantGroupName)
                  .map((g) => (
                    <div key={g.tenantGroupID} style={tagStyle}>
                      <button
                        type="button"
                        disabled={readOnly}
                        onClick={() => removeGroup(g.tenantGroupID)}
                        style={{
                          marginRight: '0.5rem',
                          background: 'transparent',
                          color: '#fff',
                          border: 'none',
                          cursor: readOnly ? 'not-allowed' : 'pointer',
                          opacity: readOnly ? 0.4 : 1,
                        }}
                      >
                        ❌
                      </button>
                      {g.tenantGroupName}
                    </div>
                  ))}
              </div>
            </div>

            {/* Buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                gap: '1rem',
                marginTop: '1.5rem',
              }}
            >
              <button type="button" style={closeBtnStyle} onClick={handleDiscard}>
                Discard
              </button>
              <button type="submit" style={nextBtnStyle}>
                Next
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.75rem',
  marginTop: '0.5rem',
  borderRadius: '6px',
  border: '1px solid #333',
  backgroundColor: '#1a1a1a',
  color: '#fff',
};

const tagContainerStyle: React.CSSProperties = {
  marginTop: '0.5rem',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  background: '#111',
  padding: '1rem',
  borderRadius: '8px',
};

const tagStyle: React.CSSProperties = {
  background: '#2a2a2a',
  padding: '0.5rem 1rem',
  borderRadius: '1rem',
  display: 'flex',
  alignItems: 'center',
  color: '#fff',
  fontSize: '0.9rem',
};

const closeBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #555',
  borderRadius: '2rem',
  cursor: 'pointer',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

const nextBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#b31317',
  color: '#fff',
  border: 'none',
  borderRadius: '2rem',
  cursor: 'pointer',
  fontWeight: 'bold',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

export default PolicyDetailForm;
